import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.js';
import {
  useBlog, useCategories, usePost, useTags, useInvalidate, useMutation, type Post,
} from '../../lib/queries.js';
import {
  Badge, Button, Card, ConfirmDialog, ErrorBox, Input, Loading, Modal, Select,
  Textarea, Toggle, STATUS_TONE, useToast, cx, Spinner, IconEye,
  IconCheck, IconWarning, IconExternal, IconChevronRight, IconPlus,
} from '../../components/ui/index.js';
import { STATUS_LABEL, date, relative, toLocalInput } from '../../lib/format.js';
import { hasRole, useAuth, useRole } from '../../lib/auth.js';
import {
  RichEditor, readAutosavePref, textOf, useAutosave, useLocalDraft, writeAutosavePref,
  type RichEditorHandle,
} from './Editor.js';
import { MediaPicker } from '../media/MediaPicker.js';
import { PreviewModal } from './PreviewModal.js';

interface Draft {
  title: string;
  slug: string;
  excerpt: string;
  contentJson: unknown;
  categoryId: string;
  tagNames: string[];
  coverImageId: string | null;
  coverImageUrl: string | null;
  metaTitle: string;
  metaDescription: string;
  noindex: boolean;
  isFeatured: boolean;
  isPinned: boolean;
}

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

function draftFromPost(p: Post): Draft {
  return {
    title: p.title,
    slug: p.slug,
    excerpt: p.excerpt ?? '',
    contentJson: p.contentJson ?? EMPTY_DOC,
    categoryId: p.category?.id ?? '',
    tagNames: p.tags.map((t) => t.name),
    coverImageId: p.coverImageId,
    coverImageUrl: p.coverImageUrl,
    metaTitle: p.metaTitle ?? '',
    metaDescription: p.metaDescription ?? '',
    noindex: p.noindex,
    isFeatured: p.isFeatured,
    isPinned: p.isPinned,
  };
}

const NEW_DRAFT: Draft = {
  title: '', slug: '', excerpt: '', contentJson: EMPTY_DOC, categoryId: '',
  tagNames: [], coverImageId: null, coverImageUrl: null,
  metaTitle: '', metaDescription: '', noindex: false, isFeatured: false, isPinned: false,
};

function SaveIndicator({
  status, lastSavedAt, dirty, auto,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: Date | null; dirty: boolean; auto: boolean;
}) {
  /**
   * ปิดบันทึกอัตโนมัติแล้วต้องเห็นชัดว่ายังมีอะไรค้างอยู่
   * ไม่งั้นผู้ใช้จะเข้าใจว่างานถูกเก็บให้เหมือนเดิมแล้วปิดแท็บทิ้ง
   */
  if (!auto && dirty) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--warn)]">
        <IconWarning size={13} /> ยังไม่ได้บันทึก
      </span>
    );
  }
  if (status === 'saving') {
    return <span className="flex items-center gap-1.5 text-xs text-[var(--ink-3)]"><Spinner className="h-3 w-3" /> กำลังบันทึก…</span>;
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--bad)]">
        <IconWarning size={13} /> บันทึกไม่สำเร็จ — กด &ldquo;บันทึก&rdquo; เพื่อลองใหม่
      </span>
    );
  }
  if (status === 'saved' && lastSavedAt) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-[var(--ok)]">
        <IconCheck size={13} /> บันทึกแล้วเมื่อ {lastSavedAt.toLocaleTimeString('th-TH')}
      </span>
    );
  }
  return null;
}

function TagInput({ value, onChange, suggestions }: {
  value: string[]; onChange: (v: string[]) => void; suggestions: string[];
}) {
  const [input, setInput] = useState('');
  const available = suggestions.filter((s) => !value.includes(s) && s.toLowerCase().includes(input.toLowerCase()));

  function add(tag: string) {
    const t = tag.trim();
    if (!t || value.includes(t) || value.length >= 20) return;
    onChange([...value, t]);
    setInput('');
  }

  return (
    <div>
      <span className="label">แท็ก</span>
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-2">
        {value.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-[var(--sunken)] px-2.5 py-1 text-xs">
            {tag}
            <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))}
                    aria-label={`ลบแท็ก ${tag}`} className="text-[var(--ink-3)] hover:text-rose-500">×</button>
          </span>
        ))}
        <input
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(input); }
            if (e.key === 'Backspace' && !input && value.length) onChange(value.slice(0, -1));
          }}
          placeholder={value.length ? '' : 'พิมพ์แล้วกด Enter'}
          className="min-w-[100px] flex-1 border-0 bg-transparent p-0.5 text-sm outline-none placeholder:text-[var(--ink-3)]"
        />
      </div>
      {input && available.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {available.slice(0, 6).map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
                    className="rounded-full border border-dashed border-[var(--line)] px-2.5 py-0.5 text-xs text-[var(--ink-3)] hover:border-[var(--brand)] hover:text-[var(--brand)]">
              + {s}
            </button>
          ))}
        </div>
      )}
      <p className="hint">แท็กที่ยังไม่มีในระบบจะถูกสร้างให้อัตโนมัติ (สูงสุด 20 แท็ก)</p>
    </div>
  );
}

export function PostEditorPage() {
  const { blogId, postId } = useParams();
  const isNew = postId === 'new' || !postId;
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidate();
  const role = useRole();
  const user = useAuth((s) => s.user);

  const blog = useBlog(blogId);
  const post = usePost(isNew ? undefined : postId);
  const categories = useCategories(blogId);
  const tags = useTags(blogId);

  const [draft, setDraft] = useState<Draft>(NEW_DRAFT);
  const [loaded, setLoaded] = useState(isNew);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [pickingImage, setPickingImage] = useState<false | 'cover' | 'inline'>(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const editorRef = useRef<RichEditorHandle>(null);
  const [autoSaveOn, setAutoSaveOn] = useState(readAutosavePref);
  const [previewing, setPreviewing] = useState(false);

  const effectiveId = postId && postId !== 'new' ? postId : createdId;

  useEffect(() => {
    if (post.data && !loaded) { setDraft(draftFromPost(post.data)); setLoaded(true); }
  }, [post.data, loaded]);

  const localDraft = useLocalDraft(effectiveId ?? `new-${blogId}`, draft, loaded);

  const set = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const payload = useMemo(() => ({
    title: draft.title || 'ไม่มีชื่อ',
    ...(draft.slug ? { slug: draft.slug } : {}),
    excerpt: draft.excerpt || null,
    contentJson: draft.contentJson,
    categoryId: draft.categoryId || null,
    tagNames: draft.tagNames,
    coverImageId: draft.coverImageId,
    metaTitle: draft.metaTitle || null,
    metaDescription: draft.metaDescription || null,
    noindex: draft.noindex,
    isFeatured: draft.isFeatured,
    isPinned: draft.isPinned,
  }), [draft]);

  const persist = useCallback(async () => {
    if (!draft.title.trim()) throw new Error('กรุณาตั้งชื่อบทความก่อนบันทึก');
    if (effectiveId) {
      await api.patch(`/v1/posts/${effectiveId}`, payload);
    } else {
      const created = await api.post<{ data: Post }>(`/v1/blogs/${blogId}/posts`, payload);
      setCreatedId(created.data.id);
      // เปลี่ยน URL เป็น id จริงโดยไม่ reload — กด refresh แล้วบทความยังอยู่
      window.history.replaceState(null, '', `/blogs/${blogId}/posts/${created.data.id}`);
    }
    localDraft.clear();
    invalidate(['posts', blogId], ['post', effectiveId], ['quota']);
  }, [draft.title, effectiveId, payload, blogId, invalidate, localDraft]);

  const autosave = useAutosave(draft, persist, {
    enabled: loaded && Boolean(draft.title.trim()),
    auto: autoSaveOn,
  });

  const publish = useMutation({
    mutationFn: async (when?: string) => {
      await persist();
      const id = effectiveId ?? createdId;
      return api.post<{ data: Post; message: string }>(`/v1/posts/${id}/publish`,
        when ? { publishedAt: new Date(when).toISOString() } : {});
    },
    onSuccess: (r) => {
      toast(r.message ?? 'เผยแพร่แล้ว');
      invalidate(['posts', blogId], ['post', effectiveId], ['blogs']);
      setPublishing(false);
      void post.refetch();
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const unpublish = useMutation({
    mutationFn: () => api.post(`/v1/posts/${effectiveId}/unpublish`),
    onSuccess: () => { toast('ถอนการเผยแพร่แล้ว'); invalidate(['posts', blogId], ['post', effectiveId]); void post.refetch(); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/v1/posts/${effectiveId}`),
    onSuccess: () => { toast('ลบบทความแล้ว'); invalidate(['posts', blogId], ['quota']); navigate(`/blogs/${blogId}`); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (blog.isLoading || (!isNew && post.isLoading)) return <Loading />;
  if (blog.error) return <ErrorBox error={blog.error} />;
  if (post.error) return <ErrorBox error={post.error} />;

  const current = post.data;
  const status = current?.status ?? 'DRAFT';
  const canPublish = hasRole(role, 'EDITOR');
  const isOwnPost = !current || current.author.id === user?.id;
  const canEdit = hasRole(role, 'EDITOR') || (role === 'AUTHOR' && isOwnPost);
  const words = textOf(draft.contentJson).length;

  return (
    <>
      {/* แถบบนสุด: ชื่อ + สถานะ + ปุ่มหลัก */}
      <div className="sticky top-0 z-30 -mx-4 mb-6 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--bg)_92%,transparent)] px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3">
          <Link
            to={`/blogs/${blogId}`}
            className="flex items-center gap-1 text-sm text-[var(--ink-3)] transition-colors hover:text-[var(--brand)]"
          >
            <IconChevronRight size={13} className="rotate-180" /> กลับ
          </Link>
          <Badge tone={STATUS_TONE[status] ?? 'gray'}>{STATUS_LABEL[status] ?? status}</Badge>
          <SaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt}
                         dirty={autosave.dirty} auto={autoSaveOn} />

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/*
              เดิมปุ่มนี้เป็นลิงก์ไปหน้าจริง จึงมีเฉพาะบทความที่เผยแพร่แล้ว
              แปลว่าฉบับร่างไม่มีทางดูหน้าตาจริงได้เลยก่อนกดเผยแพร่ ซึ่งเป็นตอนที่ต้องดูที่สุด
            */}
            {effectiveId && (
              <Button size="sm" variant="ghost" icon={<IconEye size={14} />}
                      disabled={!draft.title.trim()}
                      onClick={() => setPreviewing(true)}>ดูตัวอย่าง</Button>
            )}
            {effectiveId && current && current.revisionNo > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setShowRevisions(true)}>ประวัติแก้ไข</Button>
            )}
            {/* ใช้ Toggle ตัวเดิมของระบบ ไม่ทำสวิตช์แบบใหม่ขึ้นมาเฉพาะที่นี่ */}
            <span className="pr-1"
                  title="เปิดไว้ ระบบจะบันทึกให้เองทุก 20 วินาที · ปิดแล้วต้องกดบันทึกเอง (Cmd/Ctrl+S ก็ได้)">
              <Toggle
                label="บันทึกอัตโนมัติ"
                checked={autoSaveOn}
                onChange={(v) => { setAutoSaveOn(v); writeAutosavePref(v); }}
              />
            </span>
            <Button size="sm" variant="outline" onClick={autosave.saveNow}
                    disabled={!draft.title.trim() || autosave.status === 'saving'}>บันทึก</Button>
            {status === 'PUBLISHED' && canPublish ? (
              <Button size="sm" variant="secondary" loading={unpublish.isPending}
                      onClick={() => unpublish.mutate()}>ถอนการเผยแพร่</Button>
            ) : (
              <Button size="sm" disabled={!draft.title.trim() || !canEdit}
                      onClick={() => setPublishing(true)}>
                {canPublish ? 'เผยแพร่' : 'ส่งให้ตรวจ'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {localDraft.recovered && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <span className="flex-1 text-[var(--warn)]">
            พบฉบับร่างที่ยังไม่ได้บันทึกจากการใช้งานครั้งก่อน — ต้องการกู้คืนหรือไม่?
          </span>
          <Button size="sm" onClick={() => { setDraft(localDraft.recovered as Draft); localDraft.discard(); }}>กู้คืน</Button>
          <Button size="sm" variant="ghost" onClick={localDraft.discard}>ทิ้งไป</Button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* ซ้าย: เนื้อหา */}
        <div className="space-y-4">
          <input
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="หัวข้อบทความ"
            disabled={!canEdit}
            className="w-full border-0 bg-transparent px-1 text-3xl font-bold tracking-tight text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]"
          />
          <RichEditor
            ref={editorRef}
            value={draft.contentJson}
            onChange={(json) => set('contentJson', json)}
            onPickImage={() => setPickingImage('inline')}
            editable={canEdit}
          />
          <p className="px-1 text-xs text-[var(--ink-3)]">{words.toLocaleString('th-TH')} ตัวอักษร</p>
        </div>

        {/* ขวา: sidebar */}
        <div className="space-y-4">
          <Card className="space-y-4 p-4">
            <h3 className="text-sm font-semibold text-[var(--ink)]">รูปหน้าปก</h3>
            {draft.coverImageUrl ? (
              <div className="space-y-2">
                <img src={draft.coverImageUrl} alt="" className="aspect-video w-full rounded-lg object-cover" />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setPickingImage('cover')}>เปลี่ยนรูป</Button>
                  <Button size="sm" variant="ghost"
                          onClick={() => { set('coverImageId', null); set('coverImageUrl', null); }}>ลบรูป</Button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setPickingImage('cover')} disabled={!canEdit}
                      className="grid aspect-video w-full place-items-center rounded-lg border-2 border-dashed border-[var(--line)] text-sm text-[var(--ink-3)] hover:border-[var(--brand)] hover:text-[var(--brand)]">
                + เลือกรูปหน้าปก
              </button>
            )}
            <p className="hint">รูปนี้ใช้ทั้งใน widget, หน้าบล็อก และตอนแชร์ลิงก์ลงโซเชียล</p>
          </Card>

          <Card className="space-y-4 p-4">
            <h3 className="text-sm font-semibold text-[var(--ink)]">การจัดหมวดหมู่</h3>
            <Select label="หมวดหมู่" value={draft.categoryId} disabled={!canEdit}
                    onChange={(e) => set('categoryId', e.target.value)}>
              <option value="">— ไม่ระบุ —</option>
              {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <TagInput value={draft.tagNames} onChange={(v) => set('tagNames', v)}
                      suggestions={tags.data?.map((t) => t.name) ?? []} />
            <Toggle label="บทความแนะนำ" checked={draft.isFeatured} disabled={!canEdit}
                    onChange={(v) => set('isFeatured', v)}
                    hint="widget ที่ตั้งค่า 'เฉพาะบทความแนะนำ' จะดึงเฉพาะบทความที่ติดธงนี้" />
            <Toggle label="ปักหมุดไว้บนสุด" checked={draft.isPinned} disabled={!canEdit}
                    onChange={(v) => set('isPinned', v)}
                    hint="แสดงก่อนบทความอื่นเสมอในหน้าบล็อกและ widget" />
          </Card>

          <Card className="space-y-4 p-4">
            <h3 className="text-sm font-semibold text-[var(--ink)]">คำโปรย และ SEO</h3>
            <Textarea label="คำโปรย" rows={3} value={draft.excerpt} disabled={!canEdit}
                      onChange={(e) => set('excerpt', e.target.value)} maxLength={500}
                      hint={`ใช้ใน widget และผลการค้นหา — เว้นว่างระบบจะตัดจากเนื้อหาให้ (${draft.excerpt.length}/500)`} />
            <Input label="URL slug" value={draft.slug} disabled={!canEdit}
                   onChange={(e) => set('slug', e.target.value)}
                   hint="เว้นว่างระบบจะสร้างจากหัวข้อให้อัตโนมัติ" />
            <Input label="Meta title" value={draft.metaTitle} disabled={!canEdit}
                   onChange={(e) => set('metaTitle', e.target.value)} maxLength={255}
                   hint="เว้นว่างจะใช้หัวข้อบทความ" />
            <Textarea label="Meta description" rows={2} value={draft.metaDescription} disabled={!canEdit}
                      onChange={(e) => set('metaDescription', e.target.value)} maxLength={500}
                      hint="เว้นว่างจะใช้คำโปรย" />
            <Toggle label="ไม่ให้ Google เก็บหน้านี้ (noindex)" checked={draft.noindex} disabled={!canEdit}
                    onChange={(v) => set('noindex', v)}
                    hint="บทความที่ตั้ง noindex จะไม่ปรากฏใน widget ด้วย" />
          </Card>

          {current && (
            <Card className="space-y-2 p-4 text-sm">
              <h3 className="text-sm font-semibold text-[var(--ink)]">ข้อมูลบทความ</h3>
              <div className="flex justify-between"><span className="text-[var(--ink-3)]">ผู้เขียน</span><span>{current.author.name}</span></div>
              <div className="flex justify-between"><span className="text-[var(--ink-3)]">สร้างเมื่อ</span><span>{date(current.createdAt)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--ink-3)]">แก้ไขล่าสุด</span><span>{relative(current.updatedAt)}</span></div>
              {current.publishedAt && (
                <div className="flex justify-between"><span className="text-[var(--ink-3)]">เผยแพร่</span><span>{date(current.publishedAt, true)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-[var(--ink-3)]">ยอดอ่าน</span><span className="tabular-nums">{current.viewCount.toLocaleString('th-TH')}</span></div>
              <div className="flex justify-between"><span className="text-[var(--ink-3)]">คลิกจาก widget</span><span className="tabular-nums">{current.widgetClickCount.toLocaleString('th-TH')}</span></div>
              {canEdit && (
                <Button variant="ghost" size="sm" className="mt-2 w-full text-[var(--bad)]"
                        onClick={() => setDeleting(true)}>ลบบทความ</Button>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* --- modals --- */}
      {effectiveId && (
        <PreviewModal
          open={previewing}
          onClose={() => setPreviewing(false)}
          postId={effectiveId}
          liveUrl={current?.status === 'PUBLISHED' ? current.url : null}
          // บันทึกก่อนเสมอ ไม่งั้นตัวอย่างจะเป็นเนื้อหาเวอร์ชันก่อนหน้า
          // ซึ่งน่าสับสนกว่าการรอบันทึกอีกวินาทีเดียว
          onBeforeOpen={persist}
        />
      )}

      <MediaPicker
        open={Boolean(pickingImage)}
        onClose={() => setPickingImage(false)}
        onSelect={(media) => {
          if (pickingImage === 'cover') {
            set('coverImageId', media.id);
            set('coverImageUrl', media.variants?.md ?? media.publicUrl);
          } else {
            // แทรกตรงตำแหน่งเคอร์เซอร์ — เดิมต่อท้ายบทความเสมอ ทำให้แทรกกลางเรื่องไม่ได้
            editorRef.current?.insertImage({
              src: media.variants?.lg ?? media.publicUrl,
              alt: media.altText,
              width: media.width,
              height: media.height,
            });
          }
          setPickingImage(false);
        }}
        blogId={blogId}
      />

      <Modal
        open={publishing} onClose={() => setPublishing(false)}
        title={canPublish ? 'เผยแพร่บทความ' : 'ส่งบทความให้ตรวจ'}
        footer={<>
          <Button variant="ghost" onClick={() => setPublishing(false)}>ยกเลิก</Button>
          {canPublish && scheduleAt && (
            <Button variant="secondary" loading={publish.isPending}
                    onClick={() => publish.mutate(scheduleAt)}>ตั้งเวลาเผยแพร่</Button>
          )}
          <Button loading={publish.isPending} onClick={() => publish.mutate(undefined)}>
            {canPublish ? 'เผยแพร่ทันที' : 'ส่งให้ตรวจ'}
          </Button>
        </>}
      >
        <div className="space-y-4">
          {canPublish ? (
            <>
              <p className="text-sm text-[var(--ink-2)]">
                บทความจะปรากฏบนหน้าบล็อกและใน widget ทันทีที่เผยแพร่
              </p>
              <Input
                label="หรือตั้งเวลาเผยแพร่ล่วงหน้า" type="datetime-local"
                value={scheduleAt} min={toLocalInput(new Date().toISOString())}
                onChange={(e) => setScheduleAt(e.target.value)}
                hint="ระบบจะเผยแพร่อัตโนมัติเมื่อถึงเวลา (ตรวจทุก 5 นาที)"
              />
            </>
          ) : (
            <p className="text-sm text-[var(--ink-2)]">
              สิทธิ์ระดับผู้เขียนยังเผยแพร่เองไม่ได้ — บทความจะถูกส่งให้บรรณาธิการตรวจก่อน
            </p>
          )}
        </div>
      </Modal>

      {showRevisions && effectiveId && (
        <RevisionsModal postId={effectiveId} onClose={() => setShowRevisions(false)}
                        onRestored={() => { setShowRevisions(false); setLoaded(false); void post.refetch(); }} />
      )}

      <ConfirmDialog
        open={deleting} onClose={() => setDeleting(false)} onConfirm={() => remove.mutate()}
        loading={remove.isPending} danger confirmLabel="ลบบทความ" title="ลบบทความนี้?"
        message="บทความจะถูกถอนออกจากหน้าบล็อกและ widget ทันที (ข้อมูลยังอยู่ในระบบและกู้คืนได้)"
      />
    </>
  );
}

interface Revision {
  id: string; version: number; title: string; changeNote: string | null;
  changedBy: { id: string; name: string }; createdAt: string;
}

function RevisionsModal({ postId, onClose, onRestored }: {
  postId: string; onClose: () => void; onRestored: () => void;
}) {
  const toast = useToast();
  const [revisions, setRevisions] = useState<Revision[] | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);

  useEffect(() => {
    void api.get<{ data: Revision[] }>(`/v1/posts/${postId}/revisions`)
      .then((r) => setRevisions(r.data))
      .catch(() => setRevisions([]));
  }, [postId]);

  async function restore(version: number) {
    setRestoring(version);
    try {
      await api.post(`/v1/posts/${postId}/revisions/${version}/restore`);
      toast(`กู้คืนเป็นเวอร์ชัน ${version} แล้ว`);
      onRestored();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setRestoring(null);
    }
  }

  return (
    <Modal open onClose={onClose} title="ประวัติการแก้ไข" size="lg">
      {!revisions ? <Loading label="" /> : !revisions.length ? (
        <p className="text-sm text-[var(--ink-3)]">ยังไม่มีประวัติการแก้ไข</p>
      ) : (
        <ul className="divide-y divide-[var(--line-soft)]">
          {revisions.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--sunken)] text-xs font-bold tabular-nums">
                v{r.version}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[var(--ink)]">{r.title}</span>
                <span className="block text-xs text-[var(--ink-3)]">
                  {r.changedBy.name} · {relative(r.createdAt)}{r.changeNote ? ` · ${r.changeNote}` : ''}
                </span>
              </span>
              <Button size="sm" variant="outline" loading={restoring === r.version}
                      onClick={() => restore(r.version)}>กู้คืน</Button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-[var(--ink-3)]">
        การกู้คืนจะบันทึกเนื้อหาปัจจุบันเป็นเวอร์ชันใหม่ก่อนเสมอ — ไม่มีอะไรหายไป
      </p>
    </Modal>
  );
}
